import { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '@emotion/react'
import { Helmet } from 'react-helmet'
import {
  Button,
  Container,
  List,
  ListItem,
  Section,
} from 'chaoskit/src/components'
import { tint } from 'polished'
import qs from 'qs'
import { text } from 'chaoskit/src/assets/styles/utility'
import Portal from '@reach/portal'

import pageData from './2021.json'

import BaseLayout from '~layouts/BaseLayout'
import {
  DonutSection,
  StackedDonutSection,
} from '~components/state-of-chaos-engineering/DonutGraph'
import {
  BarSection,
  StackedBarSection,
  StaticLayoutBarGraph,
} from '~components/state-of-chaos-engineering/BarGraph'
import {
  styles,
  getChartColors,
} from '~components/state-of-chaos-engineering/utilities'
import GraphSectionIntro from '~components/state-of-chaos-engineering/GraphSectionIntro'
import { StylesHeaderVariables } from '~partials/header/UI'
import Data from '~components/Data'
import { ReactComponent as HeroSvg } from '~media/state-of-chaos-engineering/hero.svg'
import { ReactComponent as DividerLeft } from '~media/state-of-chaos-engineering/divider-left.svg'
import { ReactComponent as DividerRight } from '~media/state-of-chaos-engineering/divider-right.svg'
import { ReactComponent as DividerBlockLeft } from '~media/state-of-chaos-engineering/divider-block-left.svg'
import { ReactComponent as DividerBlockRight } from '~media/state-of-chaos-engineering/divider-block-right.svg'
import { ReactComponent as DemographicsSvg } from '~media/state-of-chaos-engineering/demographics.svg'
import { ReactComponent as ThingsBreakSvg } from '~media/state-of-chaos-engineering/things-break.svg'
import { ReactComponent as WhoFindsOutSvg } from '~media/state-of-chaos-engineering/who-finds-out.svg'
import { ReactComponent as TopPerformersSvg } from '~media/state-of-chaos-engineering/top-performers.svg'
import { ReactComponent as ChaosEngineeringTodaySvg } from '~media/state-of-chaos-engineering/chaos-engineering-today.svg'
import { ReactComponent as ResultsOfChaosExperimentsSvg } from '~media/state-of-chaos-engineering/results-of-chaos-experiments.svg'
import { ReactComponent as GremlinBottom } from '~media/state-of-chaos-engineering/gremlin-bottom.svg'
import hebLogo from '~media/logos/heb.svg'
import dynatraceLogo from '~media/logos/dynatrace.svg'
import grafanaLabsLogo from '~media/logos/grafana-labs.svg'
import epsagonLogo from '~media/logos/epsagon.svg'
import launchDarklyLogo from '~media/logos/launchdarkly.svg'
import pagerDutyLogo from '~media/logos/pagerduty.svg'
import { ContentGrid } from '~components/state-of-chaos-engineering/Content'
import Segment from '~components/state-of-chaos-engineering/Segment'
import Quote, { Quotation } from '~components/state-of-chaos-engineering/Quote'
import TextLink from '~components/TextLink'
import Icon from '~components/Icon'
import Image from '~components/Image'
import HelmetSEO from '~partials/global/HelmetSEO'
import MarketoForm from '~components/MarketoForm'
import track from '~helpers/track'

function ResponsiveBreak() {
  const theme = useTheme()

  return <br css={{ [theme.mq.small__max]: { display: 'none' } }} />
}

function Introduction({ isVisible, setVisibility }) {
  const theme = useTheme()

  return (
    <Section>
      <Container>
        <ContentGrid>
          <div>
            <div
              css={{
                direction: 'rtl',

                [theme.mq.medium]: {
                  position: 'sticky',
                  top: theme.space.base,
                },
              }}
            >
              <HeroSvg
                css={{
                  maxWidth: 250,
                  display: 'block',
                  margin: '0 auto',

                  [theme.mq.medium]: {
                    maxWidth: 'none',
                    width: '125%',
                  },
                }}
              />
            </div>
          </div>
          <div>
            <div
              css={{
                fontFamily: styles.font.mono,
                textTransform: 'uppercase',
                lineHeight: theme.lineHeight.small,
                marginBottom: theme.space.large,
                textAlign: 'center',

                [theme.mq.medium]: {
                  textAlign: 'left',
                  lineHeight: 1,
                },
              }}
            >
              <span
                css={{
                  fontWeight: 100,
                  fontSize: theme.fontSize['2xlarge'],

                  [theme.mq.medium]: {
                    fontSize: 80,
                  },
                }}
              >
                2021
              </span>
              <ResponsiveBreak />{' '}
              <span
                css={{
                  fontSize: theme.fontSize['2xlarge'],
                  fontWeight: 100,

                  [theme.mq.medium]: {
                    fontSize: 40,
                  },
                }}
              >
                state of
              </span>
              <ResponsiveBreak />{' '}
              <span
                css={{
                  fontWeight: theme.fontWeight.bold,
                  fontSize: theme.fontSize['2xlarge'],

                  [theme.mq.medium]: {
                    fontSize: 80,
                  },
                }}
              >
                Chaos Engineering
              </span>
            </div>
            <div css={{ position: 'relative', zIndex: 1 }}>
              {isVisible ? (
                <Fragment>
                  <Quotation
                    variation="introduction"
                    css={{
                      transform: 'rotate(180deg) translate(75%, 20%)',
                      opacity: theme.opacity.base,
                    }}
                  />
                  <Data>{pageData.introduction.blurb}</Data>
                  <Quotation
                    variation="introduction"
                    css={{
                      right: 0,
                      opacity: theme.opacity.base,
                      transform: 'translateY(-100%)',
                    }}
                  />
                </Fragment>
              ) : (
                <Data>{pageData.introduction.teaserBlurb}</Data>
              )}
            </div>
            {!isVisible && (
              <div
                css={{
                  marginTop: theme.space.large,

                  [theme.mq.medium]: {
                    width: '80%',
                  },
                }}
              >
                <MarketoForm
                  id="1254"
                  fieldValues={{ programID: 'PG1295A1' }}
                  onSuccess={() => {
                    track('Content downloaded', {
                      label: 'marketo-1254',
                    })

                    setVisibility(true)
                  }}
                />
              </div>
            )}
            {isVisible && (
              <Fragment>
                <div
                  css={{
                    marginTop: theme.space.large,
                    fontSize: theme.fontSize.xlarge,
                    lineHeight: theme.lineHeight.small,
                  }}
                >
                  <div css={{ fontWeight: theme.fontWeight.bold }}>
                    Kolton Andrus
                  </div>
                  <div css={{ fontWeight: 100 }}>CEO, Gremlin</div>
                </div>
              </Fragment>
            )}
          </div>
        </ContentGrid>
      </Container>
    </Section>
  )
}

Introduction.propTypes = {
  isVisible: PropTypes.bool,
  setVisibility: PropTypes.func.isRequired,
}

const KeyFindings = () => {
  const theme = useTheme()

  return (
    <Segment id="key-findings">
      <GraphSectionIntro title={pageData.findings.heading} />
      <ContentGrid>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: theme.space.large,
          }}
        >
          {pageData.findings.items.map((item, index) => (
            <div
              key={item}
              css={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: 'auto',
                justifyItems: 'start',
                fontSize: theme.fontSize.large,
                gap: theme.space.base,

                [theme.mq.medium]: {
                  fontSize: theme.fontSize.xlarge,
                },
              }}
            >
              <div
                css={{
                  width: theme.fontSize.large,
                  height: theme.fontSize.large,
                  position: 'relative',
                  transform: 'translateY(25%)',
                  top: theme.space.xsmall,
                }}
                style={{
                  color: getChartColors(pageData.findings.items.length)[index],
                }}
              >
                <div
                  css={{
                    border: '1px solid currentColor',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                  }}
                />
                <div
                  css={{
                    position: 'absolute',
                    top: theme.space.xsmall,
                    left: -theme.space.xsmall,
                    width: '100%',
                    height: '100%',
                    zIndex: -1,
                  }}
                  style={{ background: 'currentColor' }}
                />
              </div>
              <div>
                <Data>{item}</Data>
              </div>
            </div>
          ))}
        </div>
      </ContentGrid>
    </Segment>
  )
}

function translateToPercentage(dataset) {
  const total = dataset.reduce((acc, curr) => acc + curr.value, 0)

  return dataset.reduce(
    (acc, curr) =>
      acc.concat({
        id: curr.id,
        value: +((curr.value / total) * 100).toFixed(1),
      }),
    []
  )
}

function translateToMultiplePercentage(dataset) {
  const totalAnswers = 407

  return dataset.reduce(
    (acc, curr) =>
      acc.concat({
        id: curr.id,
        value: +((curr.value / totalAnswers) * 100).toFixed(1),
      }),
    []
  )
}

function GraphRenderer({ graph, graphData }) {
  return graph.type.startsWith('donut') ? (
    <DonutSection
      dataset={graphData}
      title={graph.heading}
      type={graph.type}
      graphProps={graph.graphProps}
    />
  ) : (
    <BarSection
      dataset={graphData}
      title={graph.heading}
      type={graph.type}
      graphProps={graph.graphProps}
      graphOverrides={{
        noPercentageAxis: graph.noPercentageAxis,
        noPercentageLabels: graph.noPercentageLabels,
        noAxisLeft: graph.noAxisLeft,
      }}
      colorsByIndex={graph.colorsByIndex}
      altLegend={graph.altLegend}
    />
  )
}

GraphRenderer.propTypes = {
  graph: PropTypes.object.isRequired,
  graphData: PropTypes.array.isRequired,
}

// Represents an array of data that share properties (part of an interior section)
function StackedLayoutGraphRenderer({ chunk }) {
  const theme = useTheme()

  return (
    <div
      css={{ marginTop: theme.space.large, marginBottom: theme.space.large }}
    >
      <ContentGrid>
        <div
          css={{
            fontSize: theme.fontSize.large,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1, // So it sits above overlapping graph

            [theme.mq.medium]: {
              fontSize: theme.fontSize.xlarge,
              textAlign: 'left',
            },
          }}
        >
          <Data>{chunk.heading}</Data>
        </div>
      </ContentGrid>
      {chunk.stackedLayout === 'donut' ? (
        <StackedDonutSection graphs={chunk.graphs} />
      ) : (
        <StackedBarSection graphs={chunk.graphs} />
      )}
    </div>
  )
}

StackedLayoutGraphRenderer.propTypes = {
  chunk: PropTypes.object.isRequired,
}

function GraphSelector({ graph }) {
  if (graph.staticLayout) {
    return <StaticLayoutBarGraph graph={graph} />
  }

  if (graph.stackedLayout) {
    return <StackedLayoutGraphRenderer chunk={graph} />
  }

  let graphData = graph.data

  if (!graph.noPercentConversion) {
    graphData = graph.multipleSelect
      ? translateToMultiplePercentage(graph.data)
      : translateToPercentage(graph.data)
  }

  return <GraphRenderer graph={graph} graphData={graphData} />
}

GraphSelector.propTypes = {
  graph: PropTypes.object.isRequired,
}

const Demographics = () => {
  const theme = useTheme()

  return (
    <Segment id="demographics">
      <GraphSectionIntro title={pageData.demographics.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <DemographicsSvg
            css={{
              height: 300,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                maxWidth: 'none',
                width: '150%',
                marginTop: -theme.space.large,
              },
            }}
          />
        </div>
        <div>
          <Data>{pageData.demographics.blurb}</Data>
          <div
            css={{
              marginTop: theme.space.large,
              display: 'grid',
              gridTemplateColumns: '1fr',
              lineHeight: 1,
              gap: theme.space.base,
              alignItems: 'center',

              [theme.mq.medium]: {
                gridTemplateColumns: 'auto',
                gridAutoFlow: 'column',
                gridAutoColumns: 'auto',
                justifyContent: 'start',
              },
            }}
          >
            <div
              css={{
                fontSize: theme.fontSize['4xlarge'],

                [theme.mq.medium]: {
                  fontSize: 100,
                },
              }}
            >
              <span css={{ fontWeight: theme.fontWeight.bold }}>
                {pageData.demographics.stats.amount}
              </span>
              <span css={{ fontWeight: 100, color: theme.color.primary.base }}>
                +
              </span>
            </div>
            <div
              css={{
                textTransform: 'uppercase',
                fontSize: theme.fontSize['2xlarge'],

                span: {
                  fontWeight: 100,
                },

                [theme.mq.medium]: {
                  fontSize: 40,
                },
              }}
            >
              <Data>{pageData.demographics.stats.heading}</Data>
            </div>
          </div>
        </div>
      </ContentGrid>
      {pageData.demographics.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
      <ContentGrid>
        <div>
          <Data>{pageData.demographics.belowBlurb}</Data>
        </div>
      </ContentGrid>
      {pageData.demographics.belowGraphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const WhoFindsOut = () => {
  const theme = useTheme()

  return (
    <Segment id="who-finds-out">
      <GraphSectionIntro title={pageData.whoFindsOut.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <WhoFindsOutSvg
            css={{
              height: 200,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                maxWidth: 'none',
                width: '150%',
              },
            }}
          />
        </div>
        <div>
          <Data>{pageData.whoFindsOut.blurb}</Data>
        </div>
      </ContentGrid>
      {pageData.whoFindsOut.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
      <ContentGrid>
        <div>
          <Data>{pageData.whoFindsOut.belowBlurb}</Data>
        </div>
      </ContentGrid>
      {pageData.whoFindsOut.belowGraphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const ThingsBreak = () => {
  const theme = useTheme()

  return (
    <Segment id="things-break">
      <GraphSectionIntro title={pageData.thingsBreak.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <ThingsBreakSvg
            css={{
              height: 200,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                width: '100%',
              },
            }}
          />
        </div>

        <div>
          <Data>{pageData.thingsBreak.blurb}</Data>
        </div>
      </ContentGrid>
      {pageData.thingsBreak.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
      <ContentGrid>
        <div>
          <Quote author={pageData.thingsBreak.quote.name} logo={hebLogo}>
            {pageData.thingsBreak.quote.content}
          </Quote>
          <div
            css={{
              marginTop: theme.space.large,
              textAlign: 'center',
            }}
          >
            <TextLink
              href={pageData.thingsBreak.quote.cta.link}
              className="SOCE__CustomerLink"
              css={{
                display: 'inline-block',
                fontWeight: theme.fontWeight.bold,
                color: theme.contrast.base,
                fontSize: theme.fontSize.small,
                textTransform: 'uppercase',
                letterSpacing: theme.letterSpacing.extended,
                border: '1px solid',
                borderColor: theme.color.primary.base,
                paddingTop: theme.space.small,
                paddingBottom: theme.space.small,
                paddingLeft: theme.space.base,
                paddingRight: theme.space.base,
                borderRadius: 9999,
                textDecoration: 'none !important',

                '&:hover, &:focus': {
                  color: theme.contrast.base,
                },
              }}
            >
              {pageData.thingsBreak.quote.cta.label}{' '}
              <Icon icon="caret-right" size="small" />
            </TextLink>
          </div>
        </div>
      </ContentGrid>

      <ContentGrid css={{ marginTop: theme.space.large }}>
        <div>
          <Data>{pageData.thingsBreak.belowBlurb}</Data>
        </div>
      </ContentGrid>
      {pageData.thingsBreak.belowGraphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const TopPerformers = () => {
  const theme = useTheme()

  return (
    <Segment id="top-performers">
      <GraphSectionIntro title={pageData.topPerformers.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <TopPerformersSvg
            css={{
              height: 200,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                maxWidth: 'none',
                width: '125%',
              },
            }}
          />
        </div>

        <div>
          <Data>{pageData.topPerformers.blurb}</Data>
        </div>
      </ContentGrid>
      {pageData.topPerformers.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const EvolutionOfChaosEngineering = () => {
  const theme = useTheme()

  return (
    <Segment id="evolution-of-chaos-engineering">
      <GraphSectionIntro title={pageData.evolutionOfChaosEngineering.heading} />
      <ContentGrid>
        <div>
          <Data>{pageData.evolutionOfChaosEngineering.blurb}</Data>
        </div>
      </ContentGrid>
      {pageData.evolutionOfChaosEngineering.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
      <ContentGrid>
        <Quote
          variation="blue"
          author={pageData.evolutionOfChaosEngineering.quote.name}
          meta={pageData.evolutionOfChaosEngineering.quote.meta}
          css={{
            [theme.mq.medium]: {
              width: '75%',
            },
          }}
        >
          {pageData.evolutionOfChaosEngineering.quote.content}
        </Quote>
      </ContentGrid>
      <ContentGrid>
        <div css={{ marginTop: theme.space.large }}>
          <Data>{pageData.evolutionOfChaosEngineering.belowBlurb}</Data>
        </div>
      </ContentGrid>
    </Segment>
  )
}

function TodayStat({ stat, children, variation, ...rest }) {
  const theme = useTheme()

  return (
    <div css={{ textAlign: 'center' }} {...rest}>
      <div css={{ position: 'relative', zIndex: 1 }}>
        <div
          css={{
            height: theme.height.xsmall,
            position: 'absolute',
            border: '1px solid',
            width: variation === 'alt' ? '68%' : '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            borderColor:
              variation === 'alt' ? '#7A3390' : theme.color.primary.base,
            zIndex: -1,
          }}
        />
        <div
          css={{
            fontWeight: 100,
            fontSize: 80,
            lineHeight: 1,
          }}
        >
          {stat}
        </div>
      </div>
      <div
        css={{
          marginTop: theme.space.small,
          fontWeight: theme.fontWeight.bold,
          textTransform: 'uppercase',
          letterSpacing: theme.letterSpacing.extended,
        }}
      >
        {children}
      </div>
    </div>
  )
}

TodayStat.propTypes = {
  children: PropTypes.node.isRequired,
  stat: PropTypes.string.isRequired,
  stuntBar: PropTypes.bool,
  variation: PropTypes.oneOf(['alt']),
}

const ChaosEngineeringToday = () => {
  const theme = useTheme()

  return (
    <Segment id="chaos-engineering-today">
      <GraphSectionIntro title={pageData.chaosEngineeringToday.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <ChaosEngineeringTodaySvg
            css={{
              height: 200,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                width: '100%',
              },
            }}
          />
        </div>
        <div>
          <Data>{pageData.chaosEngineeringToday.blurb}</Data>

          <div
            css={{
              marginTop: theme.space.large,
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: theme.space.large,
            }}
          >
            <TodayStat
              stat={pageData.chaosEngineeringToday.stats.platform.stat}
            >
              {pageData.chaosEngineeringToday.stats.platform.heading}
            </TodayStat>
            <TodayStat
              variation="alt"
              stat={pageData.chaosEngineeringToday.stats.k8s.stat}
            >
              {pageData.chaosEngineeringToday.stats.k8s.heading}
            </TodayStat>
          </div>
        </div>
      </ContentGrid>
      {pageData.chaosEngineeringToday.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const ResultsOfChaosExperiments = () => {
  const theme = useTheme()

  return (
    <Segment id="results-of-chaos-experiments">
      <GraphSectionIntro title={pageData.resultsOfChaosExperiments.heading} />
      <ContentGrid>
        <div css={{ direction: 'rtl' }}>
          <ResultsOfChaosExperimentsSvg
            css={{
              height: 300,
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',

              [theme.mq.medium]: {
                marginLeft: 0,
                marginRight: 0,
                height: 'auto',
                width: '100%',
              },
            }}
          />
        </div>
        <div>
          <Data>{pageData.resultsOfChaosExperiments.blurb}</Data>
        </div>
      </ContentGrid>

      {pageData.resultsOfChaosExperiments.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
    </Segment>
  )
}

const FutureOfChaosEngineering = () => {
  const theme = useTheme()

  return (
    <Segment id="future-of-chaos-engineering">
      <GraphSectionIntro title={pageData.futureOfChaosEngineering.heading} />
      {pageData.futureOfChaosEngineering.graphs.map((graph) => (
        <GraphSelector key={graph.heading} graph={graph} />
      ))}
      <ContentGrid>
        <div>
          <Data>{pageData.futureOfChaosEngineering.blurb}</Data>
        </div>
      </ContentGrid>
      <ContentGrid>
        <div
          css={{
            ...theme.text['2xlarge__fluid'],
            marginTop: theme.space.large,

            span: {
              fontWeight: 100,
            },
          }}
        >
          <Data>{pageData.futureOfChaosEngineering.belowBlurb}</Data>
        </div>
      </ContentGrid>
    </Segment>
  )
}

function ContributorLogo({ company, ...rest }) {
  let src

  // eslint-disable-next-line default-case
  switch (company) {
    case 'Dynatrace':
      src = dynatraceLogo
      break
    case 'Grafana Labs':
      src = grafanaLabsLogo
      break
    case 'Epsagon':
      src = epsagonLogo
      break
    case 'LaunchDarkly':
      src = launchDarklyLogo
      break
    case 'PagerDuty':
      src = pagerDutyLogo
      break
  }

  return <Image src={src} alt="company" {...rest} />
}

ContributorLogo.propTypes = {
  company: PropTypes.string.isRequired,
}

const Contributors = () => {
  const theme = useTheme()

  return (
    <Segment id="contributors">
      <GraphSectionIntro title={pageData.contributors.heading} />
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: theme.space.large,
        }}
      >
        {pageData.contributors.items.map((contributor, index) => (
          <div
            key={contributor.name}
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: theme.space.base,
              justifyContent: 'start',
              alignItems: 'center',

              [theme.mq.medium]: {
                gridTemplateColumns: 'auto',
                gridAutoFlow: 'column',
                gridAutoColumns: 'auto',
                gap: theme.space.large,
              },
            }}
          >
            <ContributorLogo
              company={contributor.name}
              css={{ width: 200, display: 'block', margin: '0 auto' }}
            />
            <div
              css={[
                {
                  paddingTop: theme.space.base,
                  paddingBottom: theme.space.large,

                  [theme.mq.medium]: {
                    paddingTop: theme.space.large,
                  },
                },
                index !== pageData.contributors.items.length - 1 && {
                  paddingBottom: theme.space.large,
                  borderBottom: '1px solid',
                  borderBottomColor: theme.color.primary.base,
                },
              ]}
            >
              <p css={{ marginBottom: theme.space.base }}>
                <Data>{contributor.blurb}</Data>
              </p>
              <TextLink
                href={contributor.link}
                css={{
                  fontWeight: theme.fontWeight.bold,
                  color: theme.contrast.base,

                  '&:hover, &:focus': {
                    color: theme.contrast.base,
                  },
                }}
              >
                Learn more{' '}
                <Icon
                  icon="external"
                  css={{
                    color: theme.color.primary.base,
                    marginLeft: theme.space.xsmall,
                  }}
                />
              </TextLink>
            </div>
          </div>
        ))}
      </div>
    </Segment>
  )
}

function Divider({ alignment }) {
  const theme = useTheme()

  return (
    <div
      css={{
        marginTop: theme.space.large,
        marginBottom: theme.space.large,
      }}
    >
      {alignment === 'left' && <DividerLeft />}
      {alignment === 'right' && <DividerRight />}
      {alignment === 'block-left' && <DividerBlockLeft />}
      {alignment === 'block-right' && <DividerBlockRight />}
    </div>
  )
}

Divider.propTypes = {
  alignment: PropTypes.oneOf(['left', 'right', 'block-left', 'block-right']),
}

const Navigation = ({ isVisible }) => {
  const theme = useTheme()

  const [activeNavItem, setActiveNavItem] = useState()
  const [open, setOpen] = useState(false)

  const navVariants = {
    hidden: {
      x: '100%',
    },
    visible: {
      x: 0,
    },
  }

  const navItems = [
    {
      id: 'key-findings',
      label: 'Key findings',
    },
    {
      id: 'things-break',
      label: 'Things break',
    },
    {
      id: 'who-finds-out',
      label: 'Who finds out',
    },
    {
      id: 'top-performers',
      label: 'Top performers',
    },
    {
      id: 'evolution-of-chaos-engineering',
      label: 'Evolution of Chaos Engineering',
    },
    {
      id: 'chaos-engineering-today',
      label: 'Chaos Engineering today',
    },
    {
      id: 'results-of-chaos-experiments',
      label: 'Results of chaos experiments',
    },
    {
      id: 'future-of-chaos-engineering',
      label: 'Future of Chaos Engineering',
    },
    {
      id: 'demographics',
      label: 'Demographics',
    },
    {
      id: 'contributors',
      label: 'Contributors',
    },
  ]

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          setActiveNavItem(entry.target.id)
        }
      })
    })

    if (isVisible) {
      const $sections = Array.from(document.querySelectorAll('.SOCE__Section'))

      $sections.forEach((section) => {
        observer.observe(section)
      })
    }

    // Cleanup on unmount
    return () => {
      if (observer) observer.disconnect()
    }
  }, [isVisible])

  return (
    <Fragment>
      <Button
        type="default"
        size="small"
        css={{
          position: 'fixed',
          top: theme.space.base,
          right: theme.space.base,
          zIndex: 10,
          fontFamily: styles.font.mono,
        }}
        onClick={() => setOpen(true)}
      >
        Topics
      </Button>
      <Portal>
        <AnimatePresence>
          {open && (
            <motion.nav
              variants={navVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ x: { stiffness: 1000 } }}
              className="u-contrast"
              css={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: 300,
                height: '100%',
                borderTopLeftRadius: theme.borderRadius.xlarge,
                gap: theme.space.base,
                background: theme.color.dark.base,
                zIndex: 999,
                overflow: 'auto',
                padding: theme.space.large,
                boxShadow: theme.boxShadow.large,
              }}
            >
              <Button
                type="default"
                size="small"
                iconOnly
                css={{
                  position: 'absolute',
                  top: theme.space.base,
                  right: theme.space.base,
                  fontFamily: styles.font.mono,
                  zIndex: 2,
                }}
                onClick={() => setOpen(false)}
              >
                <Icon icon="close" />
              </Button>
              <List space="base">
                {navItems.map((item) => (
                  <ListItem key={item.id}>
                    <a
                      href={`#${item.id}`}
                      css={[
                        {
                          fontSize: theme.fontSize.base,
                          color: theme.contrast.base,

                          '&:hover, &:focus': {
                            color: theme.contrast.base,
                          },
                        },

                        activeNavItem === item.id && {
                          ...text.underline,
                          color: theme.color.primary.base, // Trick since `text-decoration-color` isn't widely supported
                        },
                      ]}
                    >
                      <span css={{ color: theme.contrast.base }}>
                        {item.label}
                      </span>
                    </a>
                  </ListItem>
                ))}
              </List>
            </motion.nav>
          )}
        </AnimatePresence>
      </Portal>
    </Fragment>
  )
}

Navigation.propTypes = {
  isVisible: PropTypes.bool,
}

function PageContent({ isVisible }) {
  return (
    <div css={{ display: isVisible ? 'block' : 'none' }}>
      <Navigation isVisible={isVisible} />
      <Divider alignment="left" />
      <KeyFindings />
      <ThingsBreak />
      <Divider alignment="right" />
      <WhoFindsOut />
      <Divider alignment="block-left" />
      <TopPerformers />
      <Divider alignment="left" />
      <EvolutionOfChaosEngineering />
      <Divider alignment="right" />
      <ChaosEngineeringToday />
      <Divider alignment="block-left" />
      <ResultsOfChaosExperiments />
      <Divider alignment="block-right" />
      <FutureOfChaosEngineering />
      <Demographics />
      <Contributors />
    </div>
  )
}

PageContent.propTypes = {
  isVisible: PropTypes.bool,
}

export default function StateOfCE2021() {
  const theme = useTheme()

  const [isVisible, setVisibility] = useState(false)

  useEffect(() => {
    const queryVars = qs.parse(window?.location?.search, {
      ignoreQueryPrefix: true,
    })

    // If we have the correct query arg, bypass form data and show page
    if (queryVars?.soce2021 || window?.localStorage?.getItem('soce2021')) {
      setVisibility(true)
    }
  }, [])

  useEffect(() => {
    if (isVisible) {
      window.localStorage.setItem('soce2021', true)
    }
  }, [isVisible])

  return (
    <BaseLayout
      headerProps={{
        showNav: false,
        sticky: false,
      }}
      footerProps={{
        hideCTA: true,
      }}
      siteWrapperProps={{
        css: [
          {
            background: `linear-gradient(to right, ${
              theme.color.dark.base
            }, ${tint(0.15, theme.color.dark.base)})`,
            paddingTop: StylesHeaderVariables(theme).topOffset,
          },
        ],
      }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <Helmet>
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@100;400&family=IBM+Plex+Sans:wght@200;400;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <main
        css={{
          fontFamily: styles.font.sans,
          fontWeight: 400,
        }}
        className="u-contrast"
      >
        <Introduction isVisible={isVisible} setVisibility={setVisibility} />
        <PageContent isVisible={isVisible} />
        <Container>
          <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
            <GremlinBottom
              css={{
                width: 200,

                [theme.mq.medium]: {
                  width: 400,
                },
              }}
            />
          </div>
        </Container>
      </main>
    </BaseLayout>
  )
}
